import React from "react";
import { Link } from "react-router-dom";
import { Users, Crown, Shield, Radio, ArrowRight, Disc3, FileMusic } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

export default function MembersHub() {
  return (
    <div className="min-h-screen" data-testid="members-hub-page">
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={COAT_OF_ARMS} alt="Coat of Arms" className="h-24 mx-auto mb-6" />
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
            Access Portals
          </span>
          <h1 className="heading-hero mt-2 mb-4">Choose Your Portal</h1>
          <p className="body-large text-slate-300 max-w-3xl mx-auto">
            One account can serve Nation, Subscriber, Yashar’al Live, Record Label, and Publishing access. Each private workspace retains its own permissions.
          </p>
        </div>
      </section>

      <section className="bg-parchment py-10 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/login" className="rounded-md bg-royal-blue px-5 py-3 text-sm font-semibold text-white" data-testid="members-account-signin">Sign In — One Account</Link>
            <Link to="/portal" className="rounded-md border border-royal-blue px-5 py-3 text-sm font-semibold text-royal-blue" data-testid="members-account-portal">Open My Account Portal</Link>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <Card className="card-official hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-royal-purple/10 rounded-full flex items-center justify-center mb-4">
                  <Crown className="w-8 h-8 text-royal-purple" />
                </div>
                <CardTitle className="heading-card text-royal-blue">YaSHaRUN Access</CardTitle>
                <CardDescription>
                  For registered YaSHaRUN — The Beloved Upright of YaHUaH
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-slate-600 space-y-2 mb-6">
                  <li>Access nation governance documents</li>
                  <li>View upcoming assemblies and events</li>
                  <li>Function resources and programs</li>
                  <li>Submit proposals and feedback</li>
                </ul>
                <Link to="/nationals/login">
                  <Button className="w-full btn-outline-royal" data-testid="national-login-link">
                    Sign In as YaSHaRUN
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="card-official hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-kingdom-gold/10 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-kingdom-gold" />
                </div>
                <CardTitle className="heading-card text-royal-blue">Subscriber Access</CardTitle>
                <CardDescription>
                  For monthly supporters of The Kingdom
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-slate-600 space-y-2 mb-6">
                  <li>Monthly content downloads</li>
                  <li>Exclusive subscriber content</li>
                  <li>Manage your subscription</li>
                  <li>Early access to new releases</li>
                </ul>
                <Link to="/subscribers/login">
                  <Button className="w-full btn-gold" data-testid="subscriber-login-link">
                    Sign In as Subscriber
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="card-official hover:shadow-xl transition-shadow md:col-span-2 lg:col-span-1">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
                  <Radio className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="heading-card text-royal-blue">Yasharal Live Access</CardTitle>
                <CardDescription>
                  For meetings, recording, studio production, and broadcasting
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-slate-600 space-y-2 mb-6">
                  <li>Join or host secure meetings</li>
                  <li>Record and manage broadcasts</li>
                  <li>Use Yasharal Live Studio tools</li>
                  <li>Standalone accounts do not receive Nation or Subscriber access</li>
                </ul>
                <Link to="/yasharal-live/login">
                  <Button className="w-full bg-royal-blue text-white hover:bg-royal-blue/90" data-testid="yasharal-live-login-link">
                    Sign In to Yasharal Live
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-xs text-slate-500 mt-3 text-center">
                  YaSHaRUN may use their existing credentials. New outside users can create a Yasharal Live account.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-7 grid gap-5 md:grid-cols-2" data-testid="members-music-portals">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <Disc3 className="h-8 w-8 text-royal-blue" />
              <h2 className="mt-3 font-heading text-xl font-semibold text-royal-blue">Record Label — Artist Portal</h2>
              <p className="mt-2 text-sm text-slate-600">Artists, master recordings, releases and recording agreements under Yunah’Shach’Qal Yashar’al Records.</p>
              <Link to="/music/records" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-royal-blue underline">Enter Records Portal <ArrowRight size={15}/></Link>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <FileMusic className="h-8 w-8 text-royal-blue" />
              <h2 className="mt-3 font-heading text-xl font-semibold text-royal-blue">Publishing — Writer Portal</h2>
              <p className="mt-2 text-sm text-slate-600">Songwriters, compositions, writer splits and publishing agreements under Yunah’Shach’Qal Yashar’al Publishing.</p>
              <Link to="/music/publishing" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-royal-blue underline">Enter Publishing Portal <ArrowRight size={15}/></Link>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Need access?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/become-national">
                <Button variant="outline" className="btn-outline-royal">
                  <Users className="w-4 h-4 mr-2" />
                  Become YaSHaRUN
                </Button>
              </Link>
              <Link to="/donate">
                <Button variant="outline" className="btn-outline-royal">
                  Subscribe Monthly
                </Button>
              </Link>
              <Link to="/yasharal-live/signup">
                <Button variant="outline" className="btn-outline-royal">
                  Create Yasharal Live Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
